import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpRequest, HttpEvent, HttpHeaders, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, of, timer } from 'rxjs';
import { timeout, retryWhen, scan, delay, catchError, switchMap, concatMap } from 'rxjs/operators';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { AgentApiService } from '../../../../../shared/services/agent-api/agent-api.service';
import { AgentStreamingRequest } from '../../../models/agent-api.models';
import { environment } from 'src/environments/environment';
import config from '../../../../../config/config.json';
import agentConfig from '../../../../../config/agent_config.json';
// Agent API config is now accessed via AgentApiService (agent-specific lookups)
import mockResponses from '../../../config/api_mock_response.json';
import { TokenService } from '../../../../../shared/services/token/token.service';

/**
 * Agent Streaming API Service
 * 
 * Makes SSE streaming HTTP requests for agent responses.
 * Similar to chatbot's callChunkedApi - returns HttpEvent stream.
 * Processing logic is handled in the component (agent-chat-container).
 * 
 * Uses shared AgentApiService only for getting API URL from config.
 */
@Injectable({
  providedIn: 'root'
})
export class StreamingApiServicesService {
  private readonly http = inject(HttpClient);
  private readonly tokenService = inject(TokenService);
  
  constructor(
    private readonly logger: LoggerService,
    private readonly agentApiService: AgentApiService
  ) {}

  /**
   * Check if global mock mode is enabled in agent configuration
   */
  private isMockEnabled(): boolean {
    try {
      const mockEnabled = (agentConfig as any).api?.useMock === true;
      return mockEnabled;
    } catch (error) {
      this.logger.logError('[StreamingApiService] Error checking mock config', 'isMockEnabled', String(error));
      return false;
    }
  }

  private shouldUseMockStreaming(): boolean {
    return this.isMockEnabled();
  }

  private resolveMockChunks(agentId?: string): Array<{
    event: string;
    data: Record<string, unknown>;
    delay_ms?: number;
  }> | null {
    const responseKey = agentId === 'regulations_agent'
      ? 'get_agent_response_regulations'
      : 'get_agent_response';
    const responses = (mockResponses as {
      responses?: Record<string, { chunks?: unknown[] }>;
    }).responses;
    const mockData = responses?.[responseKey];
    return (mockData?.chunks as Array<{
      event: string;
      data: Record<string, unknown>;
      delay_ms?: number;
    }>) ?? null;
  }

  /**
   * Create mock SSE streaming response using mock data from api_mock_response.json
   */
  private createMockStreamingResponse(apiData: AgentStreamingRequest, agentId?: string): Observable<HttpEvent<any>> {
    try {
      const chunks = this.resolveMockChunks(agentId);

      if (!chunks?.length) {
        this.logger.logError('[StreamingApiService] Mock chunks not found', 'createMockStreamingResponse');
        return of({} as HttpEvent<any>);
      }

      this.logger.logInfo(
        `[StreamingApiService] Using mock streaming response for agent=${agentId || 'default'}, thread: ${apiData.thread_id}`,
        'createMockStreamingResponse'
      );

      return new Observable(observer => {
        let chunkIndex = 0;
        let accumulatedText = '';
        const streamChunks = chunks;

        const sendNextChunk = () => {
          if (chunkIndex >= streamChunks.length) {
            // Send final Response event to complete the stream
            const finalEvent: HttpEvent<any> = {
              type: HttpEventType.Response,
              status: 200,
              statusText: 'OK',
              url: '/mock/agent/streaming',
              headers: new HttpHeaders(),
              ok: true
            } as HttpResponse<any>;
            
            observer.next(finalEvent);
            observer.complete();
            return;
          }

          const chunk = streamChunks[chunkIndex];
          
          // Create SSE data line in real SSE format: "data: {...}\n\n"
          const sseData = {
            event: chunk.event,
            data: {
              ...chunk.data,
              thread_id: apiData.thread_id // Use the actual thread_id from request
            }
          };
          
          const sseDataLine = `data: ${JSON.stringify(sseData)}\n\n`;
          accumulatedText += sseDataLine;

          // Create mock DownloadProgress event with accumulated partialText
          const mockEvent: HttpEvent<any> = {
            type: HttpEventType.DownloadProgress,
            loaded: accumulatedText.length,
            total: undefined,
            partialText: accumulatedText
          } as any;

          observer.next(mockEvent);
          chunkIndex++;

          // Preserve the timing in the mock fixture so it accurately represents the API stream.
          const delayMs = chunk.delay_ms ?? 100;

          setTimeout(sendNextChunk, delayMs);
        };

        // Start sending chunks
        sendNextChunk();
      });

    } catch (error: any) {
      this.logger.logError('[StreamingApiService] Error creating mock response', 'createMockStreamingResponse', String(error));
      return of({} as HttpEvent<any>);
    }
  }

  /**
   * Agent Streaming API using Server-Sent Events (SSE)
   * Supports both real API calls and mock responses based on agent_config.json
   * 
   * @param apiData - Request data containing question, tenant_id, user_id, thread_id
   * @param agentId - Optional agent ID for agent-specific API config lookup
   * @returns Observable<HttpEvent<any>> - Stream of HTTP events for SSE response
   */
  agentStreamingApi(apiData: AgentStreamingRequest, agentId?: string): Observable<HttpEvent<any>> {
    try {
      // Validate required fields
      const { question, tenant_id, user_id, thread_id } = apiData;
      
      if (!question || !tenant_id || !user_id || !thread_id) {
        this.logger.logError(
          '[StreamingApiService] Missing required fields',
          'agentStreamingApi',
          'question, tenant_id, user_id, thread_id are required'
        );
        return of({} as HttpEvent<any>);
      }

      // Check if global mock mode is enabled
      if (this.shouldUseMockStreaming()) {
        this.logger.logInfo(
          `[StreamingApiService] Mock mode enabled for agent=${agentId || 'default'} - Using mock streaming response`,
          'agentStreamingApi'
        );
        return this.createMockStreamingResponse(apiData, agentId);
      }

      // Get API endpoint from agent-specific config
      const apiEndpoint = this.agentApiService.getApiUrlFromConfig('get_agent_response', agentId);
      
      if (!apiEndpoint) {
        this.logger.logError(
          '[StreamingApiService] API endpoint not found',
          'agentStreamingApi',
          `get_agent_response not in config for agent: ${agentId || 'unknown'}`
        );
        return of({} as HttpEvent<any>);
      }

      // Build the complete URL using agent-specific streaming base URL
      const baseUrl = this.agentApiService.getStreamingBaseUrlForAgent(agentId);
      
      if (!baseUrl) {
        this.logger.logError(
          '[StreamingApiService] Streaming base URL not found',
          'agentStreamingApi',
          `No streaming base URL configured for agent: ${agentId || 'unknown'}`
        );
        return of({} as HttpEvent<any>);
      }
      
      const url = `${baseUrl}${apiEndpoint}`;

      this.logger.logInfo(
        `[StreamingApiService] Using real API URL: ${url}`,
        'agentStreamingApi',
        `baseUrl: ${baseUrl}, endpoint: ${apiEndpoint}`
      );

      // Prepare request payload
      const requestPayload = this.buildStreamingPayload(apiData, agentId);

      // Get fresh token from TokenService
      const token = this.tokenService.getAccessToken();

      // Set headers with Bearer token, SSE support, and required CORS headers
      let headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'user-id': environment.LoginUserID || '',
        'tenant-id': environment.TenantId || '0',
        'domain-name': environment.DomainName || ''
      });

      // Add user full name if available
      if (environment.LoginUserName) {
        headers = headers.set('x-user-full-name', environment.LoginUserName);
      }

      // Create HTTP request similar to chatbot's callChunkedApi
      const request = new HttpRequest('POST', url, requestPayload, {
        headers: headers,
        reportProgress: true,
        responseType: 'text'  // Important for SSE streaming
      });

      // Return observable with retry logic (similar to chatbot)
      const response = this.http.request(request).pipe(
        timeout(90000),  // 90 second timeout
        retryWhen(errors =>
          errors.pipe(
            scan((acc, _value) => {
              if (acc >= 1) {
                throw new Error('Maximum retry attempts reached, exiting...');
              }
              return acc + 1;
            }, 0),
            delay(1000)
          )
        ),
        catchError(error => {
          this.logger.logError(
            '[StreamingApiService] Error in agentStreamingApi',
            'agentStreamingApi',
            error
          );
          throw error;
        })
      );

      return response;

    } catch (error: any) {
      this.logger.logError(
        '[StreamingApiService] Error in agentStreamingApi',
        'agentStreamingApi',
        error
      );
      return of({} as HttpEvent<any>);
    }
  }

  /**
   * Build the SSE POST body for the given agent.
   * RIA adds domain; regulations adds request_id; VIA and others use the four core fields.
   */
  private buildStreamingPayload(
    apiData: AgentStreamingRequest,
    agentId?: string
  ): Record<string, unknown> {
    const { question, tenant_id, user_id, thread_id } = apiData;
    const payload: Record<string, unknown> = {
      question,
      tenant_id,
      user_id,
      thread_id
    };

    if (apiData.selected_domains?.length) {
      payload['selected_domains'] = apiData.selected_domains;
    }
    if (apiData.available_domains?.length) {
      payload['available_domains'] = apiData.available_domains;
    }

    if (agentId === 'ria_agent') {
      payload['domain'] = apiData.domain || environment.DomainName || '';
      return payload;
    }

    if (agentId === 'regulations_agent') {
      payload['request_id'] = apiData.request_id || this.createRequestId();
    }

    return payload;
  }

  private createRequestId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `req-${Date.now()}`;
  }
}